import RotateLeftOutlinedIcon from '@mui/icons-material/RotateLeftOutlined';
import { Button, Grid, Stack } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AppMultipleDropDownMenu from '../../../../common/components/form-inputs/AppMultipleDropDownMenu';
import AppSummarySearchBar from '../../../../common/components/form-inputs/AppSummarySearchBar';
import { OrgUserPageDTO } from '@/api/DTO/user/user.interface';
import { useGetStaffListQuery } from '@/api/staff/staffApiSlice';
import { userTypeOptions } from '@/api/enum/userType.enum';
import { userDisabledOptions } from '@/api/enum/userStatus.enum';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { useGetCentreDistrictsQuery } from '@/api/area/areaApiSlice';

export default function MemberUserSummaryFilterSection() {
	const { t } = useTranslation();
	const { control, watch, resetField, setValue } =
		useFormContext<OrgUserPageDTO>();

	const myInfo = useSelector(selectMyInfo);

	const { data: getStaffList } = useGetStaffListQuery(undefined, {
		refetchOnMountOrArgChange: true,
	});
	const { data: centreDistricts, isSuccess: isGetCentreDistrictsSuccess } =
		useGetCentreDistrictsQuery(myInfo?.centre || '', {
			refetchOnMountOrArgChange: true,
			skip: !myInfo?.centre,
		});

	const resetFilterRHFState = () => {
		resetField('loginID');
		setValue('userTypes', []);
		setValue('districtIds', []);
		setValue('statuses', []);
		setValue('staffIds', []);
	};

	const clearAllFilter = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		event.preventDefault();
		resetFilterRHFState();
	};

	// reset button
	const watchLoginId = watch('loginID');
	const watchUserTypes = watch('userTypes');
	const watchStatuses = watch('statuses');
	const watchDistrictIds = watch('districtIds');
	const watchStaffIds = watch('staffIds');

	const isLoginIdDirty = watchLoginId;
	const isUserTypesDirty = watchUserTypes && watchUserTypes.length > 0;
	const isStatusDirty = watchStatuses && watchStatuses.length > 0;
	const isDistrictIdsDirty = watchDistrictIds && watchDistrictIds.length > 0;
	const isStaffIdsDirty = watchStaffIds && watchStaffIds.length > 0;
	const isFilterDirty =
		isLoginIdDirty ||
		isUserTypesDirty ||
		isStatusDirty ||
		isDistrictIdsDirty ||
		isStaffIdsDirty;

	return (
		<Stack spacing={2}>
			<Grid container spacing={3}>
				<Grid item>
					<AppSummarySearchBar
						name='loginID'
						control={control}
						placeholder={t('common.pleaseEnterKeywordSearch')}
					/>
				</Grid>
				<Grid item xs={1.7}>
					<AppMultipleDropDownMenu
						control={control}
						name='districtIds'
						label={t('common.belongedDistrict')}
						options={centreDistricts?.data ?? []}
						size='small'
						emptyArrayIfNoSelection
					/>
				</Grid>
				<Grid item xs={1.7}>
					<AppMultipleDropDownMenu
						control={control}
						name='userTypes'
						label={t('common.userCategory')}
						options={userTypeOptions}
						size='small'
						emptyArrayIfNoSelection
					/>
				</Grid>
				<Grid item xs={1.7}>
					<AppMultipleDropDownMenu
						control={control}
						name='statuses'
						label={t('common.status')}
						options={userDisabledOptions}
						size='small'
						emptyArrayIfNoSelection
					/>
				</Grid>

				<Grid item xs={1.7}>
					<AppMultipleDropDownMenu
						control={control}
						name='staffIds'
						label={t('common.staffInCharge')}
						options={getStaffList?.data ?? []}
						size='small'
						emptyArrayIfNoSelection
					/>
				</Grid>

				<Grid item xs>
					<Button
						variant='text'
						startIcon={<RotateLeftOutlinedIcon />}
						disabled={!isFilterDirty}
						onClick={clearAllFilter}
					>
						{t('button.reset')}
					</Button>
				</Grid>
			</Grid>
		</Stack>
	);
}
